"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Shell morph navbar landing — pola "Floating-on-scroll morph" (riset
 * hallmark N10 / 21st.dev, 2026-09-25):
 *
 * - ATAS (default): bar PENUH — kartu max-w-7xl sejajar halaman, bg-card
 *   SOLID tanpa blur (hemat paint backdrop-filter di area paling sering
 *   dilihat; eks blur permanen dua state).
 * - SCROLL (sentinel 1px terlewat): melebur jadi KAPSUL melayang —
 *   menyempit ke max-w-920, rounded-full, blur, turun 12px via transform.
 *
 * Empat hukum hallmark + disiplin performa morph (audit smoothness
 * 2026-09-25 — "kurang smooth" dulu karena PADDING ikut bertransisi
 * (anak nav lompat ekstra 20px + reflow) dan blur aktif di dua state):
 * 1. TINGGI TOTAL KONSTAN (outer selalu 68px = --landing-nav-h) → nol
 *    layout shift; kompensasi -mt hero tetap valid di kedua state.
 * 2. Offset visual via transform translateY(12px) — bukan padding/margin.
 * 3. PADDING KONSTAN dua state (px-4 sm:px-6 lg:px-10) — jaga alignment
 *    kontainer (logo x=120) & anak tidak bergeser ekstra; max-width jadi
 *    satu-satunya layout-prop (konsekuensi DOM tunggal) — dikurung
 *    contain:layout + will-change.
 * 4. SATU kurva waktu utk semua properti: cubic-bezier(0.16,1,0.3,1)
 *    300ms (eks 400ms — lebih snappy terasa smooth saat scroll cepat).
 *
 * Trigger = IntersectionObserver sentinel 1px + boolean-flip guard — nol
 * kerja per-frame. Children dirender server (slot auth Suspense tetap RSC
 * murni, lewat prop children). motion-reduce: transisi dimatikan.
 */
export function LandingNavMorph({ children }: { children: ReactNode }) {
  const [floating, setFloating] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const past = !entries[0]?.isIntersecting;
        setFloating((prev) => (prev === past ? prev : past)); // flip guard
      },
      { rootMargin: "-1px 0px 0px 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" className="absolute top-0 h-px w-px" />
      <header
        role="banner"
        data-floating={floating}
        className="sticky top-0 z-50 flex h-[68px] items-center"
      >
        <nav
          aria-label="Navigasi utama"
          style={{ transitionTimingFunction: EASE }}
          className={`mx-auto flex h-14 w-full items-center justify-between gap-4 border px-4 transition-[max-width,border-radius,transform,background-color] duration-300 ease-out motion-reduce:transition-none sm:px-6 lg:px-10 ${
            floating
              ? "border-border/70 bg-card/85 max-w-[920px] translate-y-3 rounded-full backdrop-blur-md"
              : "border-border/60 bg-card max-w-7xl rounded-2xl"
          } will-change-[max-width,border-radius,transform] [contain:layout]`}
        >
          {children}
        </nav>
      </header>
    </>
  );
}
